import { useEffect, useState, type ClipboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Cloud, Download, KeyRound, LogOut, RefreshCw, Trash2 } from 'lucide-react';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { useI18n } from '../i18n';
import { hasLocalData } from '../db/repo';
import { api, ApiError, NetworkError } from '../sync/api';
import { decideLink, linkAccount, pendingChanges, previousAccount, serverHasData, signOutLocal, syncNow, type SessionInfo } from '../sync/engine';
import { useSyncState } from '../sync/store';
import { Field } from '../ui/Field';
import { Sheet } from '../ui/Sheet';
import { useToast } from '../ui/Toast';
import { PasskeysSection, SessionsSection } from './AccountSecurity';
import { downloadText } from '../lib/download';
import { RejectedPanel } from './RejectedPanel';
import { AccountBenefits } from './AccountInfo';
import { CODES_PER_WINDOW, codeFromPaste, nextResendAt } from '../lib/otpCode';

export function AccountScreen() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const sync = useSyncState();
  return (
    <div className="stack-l">
      <header className="row">
        <button type="button" className="icon-btn" onClick={() => navigate('/settings')} aria-label={t('common.back')}>
          <ArrowLeft size={22} aria-hidden="true" />
        </button>
        <h1>{t('sync.title')}</h1>
      </header>
      {sync.signedIn ? <SignedIn /> : <SignIn />}
    </div>
  );
}

function errorText(t: (k: string) => string, err: unknown): string {
  if (err instanceof NetworkError) return t('sync.errors.network');
  if (err instanceof ApiError) {
    if (err.code === 'invalid_email') return t('sync.errors.invalid_email');
    if (err.code === 'invalid_code') return t('sync.errors.invalid_code');
    if (err.code === 'too_many' || err.status === 429) return t('sync.errors.too_many');
    if (err.code === 'passkey_failed') return t('sync.errors.passkey_failed');
  }
  return t('sync.errors.generic');
}

type AuthOptions = Parameters<typeof startAuthentication>[0]['optionsJSON'];

function SignIn() {
  const { t, tag } = useI18n();
  const toast = useToast();
  const sync = useSyncState();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ session: SessionInfo; otherAccount: string | null } | null>(null);
  const [devMailbox, setDevMailbox] = useState(false);
  const [sentAt, setSentAt] = useState<number[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [resent, setResent] = useState(false);

  useEffect(() => {
    api<{ devMailbox: boolean }>('/config')
      .then((c) => setDevMailbox(c.devMailbox))
      .catch(() => {});
  }, []);

  // Reloj de la cuenta atrás, solo mientras se espera el código.
  useEffect(() => {
    if (step !== 'code') return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [step]);

  const sendCode = async (again: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await api('/auth/email/start', { body: { email } });
      const at = Date.now();
      setSentAt((s) => [...s, at]);
      setNow(at);
      setResent(again);
      if (!again) setCode('');
      setStep('code');
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };
  const resend = nextResendAt(sentAt, now);
  const waitS = Math.max(0, Math.ceil((resend.at - now) / 1000));

  const afterLogin = async () => {
    const session = await api<SessionInfo>('/auth/session');
    // FR-SYNC-08: se pregunta antes de mezclar si hay datos en ambos lados o si los
    // datos del dispositivo son de otra cuenta.
    const [local, remote, prev] = await Promise.all([hasLocalData(), serverHasData(), previousAccount()]);
    const decision = decideLink({ hasLocal: local, hasRemote: remote, previousUserId: prev?.id ?? null, userId: session.user.id });
    if (decision !== 'merge') {
      setPending({ session, otherAccount: decision === 'ask_other_account' ? (prev?.email ?? '') : null });
      return;
    }
    await linkAccount(session, 'merge');
    toast.show(t('sync.signedInAs', { email: session.user.email }));
  };

  if (sync.serverAvailable === false) {
    return (
      <div className="banner banner--info">
        <Cloud size={22} aria-hidden="true" />
        <div className="banner__body">
          <span>{t('sync.unavailable')}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="card stack" aria-labelledby="account-benefits-title">
        <h2 id="account-benefits-title">{t('account.title')}</h2>
        <AccountBenefits />
      </section>
      <section className="form-section">
        {step === 'email' ? (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void sendCode(false);
            }}
          >
            <Field label={t('sync.email')} error={error}>
              {(p) => <input {...p} type="email" autoComplete="email webauthn" inputMode="email" required className="input" value={email} onChange={(e) => setEmail(e.target.value)} />}
            </Field>
            <button type="submit" className="btn btn--primary btn--block" disabled={busy || !email}>
              {t('sync.sendCode')}
            </button>
          </form>
        ) : (
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError(null);
              try {
                await api('/auth/email/verify', { body: { email, code: code.trim() } });
                await afterLogin();
              } catch (err) {
                setError(errorText(t, err));
              } finally {
                setBusy(false);
              }
            }}
          >
            <p className="small" role="status">
              {t(resent ? 'sync.codeResent' : 'sync.codeSent', { email })}
            </p>
            {devMailbox && <p className="muted xsmall">{t('sync.devMailbox')}</p>}
            <Field label={t('sync.code')} help={t('sync.codeHelp')} error={error}>
              {(p) => (
                <input
                  {...p}
                  className="input num"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  // Solo se lee lo que la persona pega (sin acceder al portapapeles por nuestra cuenta).
                  onPaste={(e: ClipboardEvent<HTMLInputElement>) => {
                    const pasted = codeFromPaste(e.clipboardData.getData('text'));
                    if (!pasted) return;
                    e.preventDefault();
                    setCode(pasted);
                  }}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              )}
            </Field>
            <button type="submit" className="btn btn--primary btn--block" disabled={busy || code.length !== 6}>
              {t('sync.verify')}
            </button>
            <div className="stack" style={{ gap: 'var(--space-1)' }}>
              <button type="button" className="btn btn--outline" disabled={busy || waitS > 0} onClick={() => void sendCode(true)}>
                {t('sync.resend')}
              </button>
              <p className="muted xsmall" aria-live="polite" data-testid="resend-hint">
                {waitS > 0
                  ? resend.capped
                    ? t('sync.resendCapped', { count: CODES_PER_WINDOW, time: new Date(resend.at).toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' }) })
                    : t('sync.resendIn', { count: waitS })
                  : t('sync.notArrived')}
              </p>
            </div>
            <button type="button" className="btn btn--ghost" onClick={() => setStep('email')}>
              {t('sync.otherEmail')}
            </button>
          </form>
        )}
        {browserSupportsWebAuthn() && (
          <button
            type="button"
            className="btn btn--outline btn--block"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const { challengeId, options } = await api<{ challengeId: string; options: AuthOptions }>('/auth/passkey/login/options', { body: {} });
                const response = await startAuthentication({ optionsJSON: options });
                await api('/auth/passkey/login/verify', { body: { challengeId, response } });
                await afterLogin();
              } catch (err) {
                setError(err instanceof ApiError || err instanceof NetworkError ? errorText(t, err) : t('sync.errors.passkey_failed'));
              } finally {
                setBusy(false);
              }
            }}
          >
            <KeyRound size={18} aria-hidden="true" /> {t('sync.passkey')}
          </button>
        )}
        {browserSupportsWebAuthn() && <p className="muted xsmall">{t('sync.passkeyWhat')}</p>}
      </section>

      <Sheet
        open={pending !== null}
        onClose={() => setPending(null)}
        title={pending?.otherAccount !== null && pending ? t('sync.otherAccountTitle') : t('sync.mergeTitle')}
        description={
          pending?.otherAccount !== null && pending
            ? t('sync.otherAccountBody', { previous: pending.otherAccount || t('sync.otherAccountUnknown'), current: pending.session.user.email })
            : t('sync.mergeBody')
        }
      >
        <button
          type="button"
          className={pending?.otherAccount != null ? 'btn btn--outline btn--block' : 'btn btn--primary btn--block'}
          onClick={async () => {
            const s = pending!.session;
            setPending(null);
            await linkAccount(s, 'merge');
          }}
        >
          {pending?.otherAccount != null ? t('sync.otherAccountMerge') : t('sync.mergeKeep')}
        </button>
        <button
          type="button"
          className="btn btn--danger btn--block"
          onClick={async () => {
            const s = pending!.session;
            setPending(null);
            await linkAccount(s, 'discard');
          }}
        >
          {t('sync.mergeDiscard')}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--block"
          onClick={async () => {
            setPending(null);
            await api('/auth/logout', { body: {} }).catch(() => {});
          }}
        >
          {t('common.cancel')}
        </button>
      </Sheet>
    </>
  );
}

function SignedIn() {
  const { t, tag } = useI18n();
  const toast = useToast();
  const sync = useSyncState();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [unsynced, setUnsynced] = useState<{ wipe: boolean; all: boolean; count: number } | null>(null);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status =
    sync.phase === 'syncing'
      ? t('sync.status.syncing')
      : sync.phase === 'offline'
        ? t('sync.status.offline')
        : sync.phase === 'error'
          ? t('sync.status.error')
          : sync.pending > 0
            ? t('sync.status.pending', { count: sync.pending })
            : t('sync.status.idle');
  const last = sync.lastSyncAt ? new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' }).format(sync.lastSyncAt) : t('sync.never');

  const signOut = async (wipe: boolean, all: boolean) => {
    await api(all ? '/auth/logout-all' : '/auth/logout', { body: {} }).catch(() => {});
    await signOutLocal(wipe);
    if (wipe) window.location.assign('/');
  };

  // Cerrar sesión vacía la cola de cambios: antes se intenta sincronizar y, si
  // aún quedan cambios sin enviar, se avisa (con borrado se perderían).
  const logout = async (wipe: boolean, all = false) => {
    await syncNow().catch(() => {});
    const count = await pendingChanges();
    if (count > 0) {
      setUnsynced({ wipe, all, count });
      return;
    }
    await signOut(wipe, all);
  };

  return (
    <>
      <section className="form-section" aria-live="polite">
        <p style={{ fontWeight: 750 }}>{t('sync.signedInAs', { email: sync.email ?? '' })}</p>
        <p className="small" data-testid="sync-status">
          {status}
        </p>
        <p className="muted xsmall">{t('sync.lastSync', { time: last })}</p>
        {sync.lastError === 'session_expired' && (
          <div className="banner" role="alert">
            <div className="banner__body">
              <strong>{t('sync.sessionExpiredTitle')}</strong>
              <span className="small">{t('sync.sessionExpiredBody')}</span>
              <div className="row">
                {/* Conserva los datos: al volver a entrar se ofrecen para combinarlos con la cuenta. */}
                <button type="button" className="btn btn--primary btn--sm" onClick={() => void signOutLocal(false)}>
                  {t('sync.sessionExpiredAction')}
                </button>
              </div>
            </div>
          </div>
        )}
        {sync.rejected > 0 && <RejectedPanel count={sync.rejected} />}
        <button type="button" className="btn btn--outline" onClick={() => void syncNow()}>
          <RefreshCw size={18} aria-hidden="true" /> {t('sync.syncNow')}
        </button>
      </section>

      <section className="stack" aria-labelledby="account-data-title">
        <h2 id="account-data-title">{t('sync.accountDataTitle')}</h2>
        <p className="muted small">{t('sync.accountDataHelp')}</p>
        <button
          type="button"
          className="btn btn--outline"
          onClick={async () => {
            try {
              const file = await api<unknown>('/account/export');
              downloadText(`habitos-cuenta-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(file, null, 2), 'application/json');
            } catch {
              toast.show(t('security.actionFailed'));
            }
          }}
        >
          <Download size={18} aria-hidden="true" /> {t('sync.accountDataDownload')}
        </button>
      </section>

      <SessionsSection />
      <PasskeysSection />

      <section className="list-card">
        <button type="button" className="list-item" onClick={() => void logout(false)}>
          <LogOut size={20} aria-hidden="true" />
          <span className="list-item__body">{t('sync.logout')}</span>
        </button>
        <button type="button" className="list-item" onClick={() => void logout(true)}>
          <LogOut size={20} aria-hidden="true" />
          <span className="list-item__body">{t('sync.logoutWipe')}</span>
        </button>
        <button type="button" className="list-item" onClick={() => void logout(false, true)}>
          <LogOut size={20} aria-hidden="true" />
          <span className="list-item__body">{t('sync.logoutAll')}</span>
        </button>
        <button type="button" className="list-item" style={{ color: 'var(--danger)' }} onClick={() => setConfirmDelete(true)}>
          <Trash2 size={20} aria-hidden="true" />
          <span className="list-item__body">{t('sync.deleteAccount')}</span>
        </button>
      </section>

      <Sheet
        open={unsynced !== null}
        onClose={() => setUnsynced(null)}
        title={t('sync.unsyncedTitle')}
        description={unsynced ? t(unsynced.wipe ? 'sync.unsyncedWipeBody' : 'sync.unsyncedKeepBody', { count: unsynced.count }) : undefined}
      >
        <button type="button" className="btn btn--primary btn--block" onClick={() => setUnsynced(null)}>
          {t('sync.unsyncedStay')}
        </button>
        <button
          type="button"
          className="btn btn--danger btn--block"
          onClick={async () => {
            const u = unsynced!;
            setUnsynced(null);
            await signOut(u.wipe, u.all);
          }}
        >
          {t('sync.unsyncedLeave')}
        </button>
      </Sheet>

      <Sheet open={confirmDelete} onClose={() => setConfirmDelete(false)} title={t('sync.deleteAccount')} description={t('sync.deleteAccountBody')}>
        {!codeSent ? (
          <button
            type="button"
            className="btn btn--danger btn--block"
            onClick={async () => {
              try {
                await api('/account/reauth/start', { body: {} });
                setCodeSent(true);
              } catch (err) {
                setError(errorText(t, err));
              }
            }}
          >
            {t('sync.sendCode')}
          </button>
        ) : (
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api('/account/delete', { body: { code } });
                await signOutLocal(true);
                toast.show(t('sync.accountDeleted'));
                window.location.assign('/');
              } catch (err) {
                setError(errorText(t, err));
              }
            }}
          >
            <Field label={t('sync.code')} error={error}>
              {(p) => <input {...p} className="input num" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />}
            </Field>
            <button type="submit" className="btn btn--danger btn--block" disabled={code.length !== 6}>
              {t('sync.deleteAccountConfirm')}
            </button>
          </form>
        )}
        {error && !codeSent && <p className="field__error">{error}</p>}
      </Sheet>
    </>
  );
}
